import {
  HARMONY_FIRST_SESSION_ID,
  entrySessionOf,
  isRootSession,
  roomOfSession,
  type WorldRoomIndex,
  type WorldRoomEntry,
  type WorldRoomDirectory,
  type WorldSessionProjection,
} from "@cleo/contracts";
import { useCloudConversations } from "./cloudConversations";
import { useLocation, useNavigate } from "@tanstack/react-router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

import type { EnvironmentThreadShell } from "@cleo/client-runtime/state/models";

import { toastManager } from "../components/ui/toast";
import { parseSessionSessionLeafPath } from "../worldViewRoute";
import { isWebHost } from "../webHost";
import { useThreadShells } from "../state/entities";
import { localModeActive } from "./worldSelection";
import {
  hydrateWorldSessionDecks,
  keepDeckFrontOnlyFor,
  requestDeckFront,
  startSessionDeckSync,
  withdrawDeckFront,
} from "./boardDeckSync";
import { activateOrCreateWorkroom } from "./workroomDoor";
import { deckDefaultBoardKind, workroomTitleForBoard } from "./workroomLaunch";
import { seatWorkroomDoor, type WorkroomOpenIntent } from "./worldViewController";
import {
  readWorldRoomEntry,
  readWorldRoomIndex,
  type WorldRoomPagesProgress,
} from "./worldSessionClient";
import {
  displaySelectedWorldEntries,
  displayWorldRoomEntries,
  displayWorldRoomPages,
  displayWorldRoomPagesWithEntries,
  isWorldRoomDisplayComplete,
  type WorldRoomDisplay,
  type WorldRoomPagesDisplay,
} from "./worldRoomDisplay";
import { forgetWorldRoom, readWorldRoom, useNeucleosWorldStore } from "./worldStore";

export type WorldViewState =
  | { readonly kind: "local" }
  | { readonly kind: "world-loading"; readonly worldKey: string }
  | {
      readonly kind: "world-unavailable";
      readonly worldKey: string | null;
      readonly reason: string;
      /** The server's typed refusal kind (e.g. `grant_stale`), null when no read happened. */
      readonly refusalKind: string | null;
      /** The automatic re-read a `transport` refusal is waiting on; null once the budget is spent. */
      readonly retry?: { readonly attempt: number; readonly of: number } | null;
    }
  | {
      readonly kind: "world-ready";
      readonly worldKey: string;
      readonly index: WorldRoomDisplay;
      readonly activeSessionId: string;
    };

/**
 * A `transport` refusal is the door not answering (its time limit, a reset
 * socket, the local server restarting), not an answer. The first rooms read
 * after a backend restart is cold and can exceed the limit (2026-09-21 ~04:14Z;
 * the same door answered in 777 ms a minute later), so the view re-reads on
 * its own after each of these delays, then stops and leaves the manual Retry.
 * A typed refusal is the door's answer and is never re-read.
 */
const TRANSPORT_RETRY_DELAYS_MS = [2_000, 4_000, 8_000, 16_000, 32_000] as const;

type WorldRoomRead =
  | { readonly status: "idle" | "loading" }
  | {
      /** Some pages of the room index are in (and the saved decks); the rest are still arriving. */
      readonly status: "partial";
      readonly scope: string;
      readonly worldKey: string;
      readonly pages: WorldRoomPagesDisplay;
    }
  | {
      readonly status: "ready";
      readonly scope: string;
      readonly worldKey: string;
      readonly index: WorldRoomIndex;
    }
  | {
      readonly status: "unavailable";
      readonly scope: string;
      readonly worldKey: string;
      readonly reason: string;
      readonly refusalKind: string;
      /** Automatic re-reads already spent on this transport outage. */
      readonly retried?: number;
    };

/**
 * One read of the room index with its saved decks; null when the caller no
 * longer applies. The decks are read alongside the pages, and `publish` gets
 * an incomplete (partial) read as soon as the decks are in and a page is, then
 * again with each further page. The returned read is the complete index, or
 * the same refusal as before: a partial is never returned as the answer.
 */
async function readRoomsWithDecks(
  worldKey: string,
  scope: string,
  isCurrent: () => boolean,
  publish?: (partial: WorldRoomRead) => void,
): Promise<WorldRoomRead | null> {
  // RM9: every session's deck rides in from the view-prefs door with the index.
  // Do not let canvases seed a default board before the saved deck arrives:
  // nothing is published until the decks are in.
  const decks = hydrateWorldSessionDecks(worldKey, fetch, undefined, isCurrent).catch(
    () => false as const,
  );
  let decksIn = false;
  let latest: WorldRoomPagesProgress | null = null;
  let indexDone = false;
  const emit = () => {
    if (publish === undefined || indexDone || !decksIn || latest === null || !isCurrent()) return;
    publish({
      status: "partial",
      scope,
      worldKey,
      pages: displayWorldRoomPages(latest),
    });
  };
  if (publish !== undefined)
    void decks.then((restored) => {
      if (restored === false) return;
      decksIn = true;
      emit();
    });
  const outcome = await readWorldRoomIndex(worldKey, undefined, {
    isCurrent,
    ...(publish === undefined
      ? {}
      : {
          onPage: (progress) => {
            latest = progress;
            emit();
          },
        }),
  });
  indexDone = true;
  if (!isCurrent()) return null;
  if (!outcome.ok)
    return {
      status: "unavailable",
      scope,
      worldKey,
      reason: outcome.reason,
      refusalKind: outcome.kind,
    };
  const restored = await decks;
  if (!isCurrent()) return null;
  return restored === false
    ? {
        status: "unavailable",
        scope,
        worldKey,
        reason: "The saved session tabs could not be read. Retry the World connection.",
        refusalKind: "view_prefs_unavailable",
      }
    : { status: "ready", scope, worldKey, index: outcome.index };
}

interface WorldViewValue {
  readonly state: WorldViewState;
  readonly pendingLabel: string | null;
  readonly beginWait: (label: string) => () => void;
  readonly refresh: () => void;
  readonly acceptReadback: (index: WorldRoomIndex) => void;
  readonly acceptEntryReadback: (entry: WorldRoomEntry) => boolean;
}

const WorldViewContext = createContext<WorldViewValue | null>(null);

export function resolveActiveWorldSession(input: {
  readonly index: WorldRoomDirectory;
  readonly worldKey: string;
  readonly pathname: string;
  readonly shells: ReadonlyArray<EnvironmentThreadShell>;
}): string {
  const ids = new Set(input.index.sessions.map((slice) => slice.sessionId));
  const leaf = parseSessionSessionLeafPath(input.pathname);
  if (leaf?.kind === "session" && ids.has(leaf.sessionId)) return leaf.sessionId;
  if (leaf?.kind === "thread") {
    const shell = input.shells.find(
      (candidate) =>
        candidate.environmentId === leaf.environmentId &&
        candidate.id === leaf.threadId &&
        candidate.worldKey === input.worldKey,
    );
    if (shell?.sessionId != null && ids.has(shell.sessionId)) return shell.sessionId;
  }
  const entry = entrySessionOf(input.index)?.sessionId ?? null;
  return entry !== null && ids.has(entry)
    ? entry
    : (input.index.sessions[0]?.sessionId ?? HARMONY_FIRST_SESSION_ID);
}

export interface WorldSessionRow {
  /** null at level 0: the row's sessions hang under the room itself. */
  readonly parentId: string | null;
  readonly roomId: string;
  readonly sessions: ReadonlyArray<WorldSessionProjection>;
}

export interface WorldSessionViewSeat {
  readonly slice: WorldSessionProjection;
  readonly execution: EnvironmentThreadShell | null;
  readonly executionCount: number;
}

/**
 * The addressed Session's ancestor chain, root first. This is a projection of
 * World authority, never a second writable "open rail" tree.
 */
export function projectWorldSessionLineage(
  index: WorldRoomDirectory,
  activeSessionId: string,
): ReadonlyArray<WorldSessionProjection> {
  const byId = new Map(index.sessions.map((slice) => [slice.sessionId, slice] as const));
  const lineage: WorldSessionProjection[] = [];
  const visited = new Set<string>();
  let cursor = byId.get(activeSessionId);
  while (cursor !== undefined && !visited.has(cursor.sessionId)) {
    lineage.push(cursor);
    visited.add(cursor.sessionId);
    cursor = isRootSession(cursor) ? undefined : byId.get(cursor.parentId);
  }
  lineage.reverse();
  return lineage;
}

function compareExecutionRecency(
  left: EnvironmentThreadShell,
  right: EnvironmentThreadShell,
): number {
  // Provider shutdown, title updates and other metadata also touch updatedAt.
  // They must not switch the visible conversation (including during restart).
  // A new thread fronts immediately; an existing one fronts on a new ask.
  const activity = (right.latestUserMessageAt ?? right.createdAt).localeCompare(
    left.latestUserMessageAt ?? left.createdAt,
  );
  if (activity !== 0) return activity;
  const created = right.createdAt.localeCompare(left.createdAt);
  if (created !== 0) return created;
  const environment = left.environmentId.localeCompare(right.environmentId);
  return environment !== 0 ? environment : left.id.localeCompare(right.id);
}

/**
 * SR-CAN-05: every Session's standing executions — the same worldKey /
 * sessionId / not-archived filter and recency law the primitive seats use,
 * every session kept in order, projected for a whole list at once so the
 * session rail can nest a plural slice's rows under its chip.
 */
export function projectSessionExecutionLists(
  shells: ReadonlyArray<EnvironmentThreadShell>,
  worldKey: string,
): ReadonlyMap<string, ReadonlyArray<EnvironmentThreadShell>> {
  const candidatesBySession = new Map<string, EnvironmentThreadShell[]>();
  for (const shell of shells) {
    if (shell.worldKey !== worldKey || shell.sessionId == null || shell.archivedAt !== null)
      continue;
    const candidates = candidatesBySession.get(shell.sessionId);
    if (candidates === undefined) candidatesBySession.set(shell.sessionId, [shell]);
    else candidates.push(shell);
  }
  for (const candidates of candidatesBySession.values()) {
    candidates.sort(compareExecutionRecency);
  }
  return candidatesBySession;
}

/**
 * FD-CAN-20: each Session's ONE standing execution — the plural fold's head,
 * never a second filter (DL4).
 */
export function projectSessionExecutions(
  shells: ReadonlyArray<EnvironmentThreadShell>,
  worldKey: string,
): ReadonlyMap<string, EnvironmentThreadShell> {
  const bySession = new Map<string, EnvironmentThreadShell>();
  for (const [sessionId, candidates] of projectSessionExecutionLists(shells, worldKey)) {
    const head = candidates[0];
    if (head !== undefined) bySession.set(sessionId, head);
  }
  return bySession;
}

/**
 * Join World-owned Session identity to local execution history. The newest
 * non-archived exact binding is the visible seat; titles, asks, parent thread
 * ids, and Goals are deliberately not fallback keys. The count remains
 * visible to callers so legacy duplicate bindings are not mistaken for a
 * one-to-one authority relationship.
 */
export function projectWorldSessionViewSeats(input: {
  readonly index: WorldRoomDirectory;
  readonly activeSessionId: string;
  readonly worldKey: string;
  readonly shells: ReadonlyArray<EnvironmentThreadShell>;
}): ReadonlyArray<WorldSessionViewSeat> {
  const candidatesBySession = projectSessionExecutionLists(input.shells, input.worldKey);
  return projectWorldSessionLineage(input.index, input.activeSessionId).map((slice) => {
    const candidates = candidatesBySession.get(slice.sessionId) ?? [];
    return {
      slice,
      execution: candidates[0] ?? null,
      executionCount: candidates.length,
    };
  });
}

export function projectWorldSessionRows(
  index: WorldRoomDirectory,
  activeSessionId: string,
): ReadonlyArray<WorldSessionRow> {
  const childrenByParent = new Map<string, WorldSessionProjection[]>();
  for (const slice of index.sessions) {
    const siblings = childrenByParent.get(slice.parentId);
    if (siblings === undefined) childrenByParent.set(slice.parentId, [slice]);
    else siblings.push(slice);
  }
  const lineage = projectWorldSessionLineage(index, activeSessionId);
  // Level 0 is the ACTIVE ROOM's root sessions (their parent is the room);
  // the World's standing room when nothing is addressed yet.
  const roomId = roomOfSession(index, activeSessionId)?.roomId ?? index.rootRoomId;
  const rows: WorldSessionRow[] = [
    { parentId: null, roomId, sessions: childrenByParent.get(roomId) ?? [] },
  ];
  for (const parent of lineage) {
    // A childless level owns no descendant row. Descendant tabs exist when a
    // spawn creates them; first children are born from the rail's
    // verbs, never from creator-only strip chrome.
    const children = childrenByParent.get(parent.sessionId);
    if (children === undefined) continue;
    rows.push({ parentId: parent.sessionId, roomId, sessions: children });
  }
  return rows;
}

export type WorldEntryView =
  | { readonly kind: "session"; readonly sessionId: string }
  | { readonly kind: "thread"; readonly environmentId: string; readonly threadId: string };

/**
 * Cold entry at "/" restores the World's own remembered room (WS8): a
 * remembered Session must still stand in the index, and a remembered session
 * leaf must still be a shell bound to this World and a live Session. Anything
 * stale — a deleted Session, a foreign or unbound thread, a draft — falls to
 * the authoritative root Session. Restoration is asked only after the World's
 * index has been read back, never before scope validates.
 */
export function resolveWorldEntryView(input: {
  readonly index: WorldRoomDirectory;
  readonly worldKey: string;
  readonly shells: ReadonlyArray<EnvironmentThreadShell>;
  readonly remembered: string;
}): WorldEntryView {
  const leaf = parseSessionSessionLeafPath(input.remembered);
  // A headless (closed) Session never restores: re-addressing it would seat
  // its tab again the moment the room opens (NV2 keeps addressed Sessions
  // visible). Closed rooms fall to the authoritative root like stale ones.
  const engagedSession = (sessionId: string): boolean =>
    input.index.sessions.some(
      (slice) => slice.sessionId === sessionId && slice.engagement !== "headless",
    );
  if (leaf?.kind === "session" && engagedSession(leaf.sessionId)) {
    return { kind: "session", sessionId: leaf.sessionId };
  }
  if (leaf?.kind === "thread") {
    const shell = input.shells.find(
      (candidate) =>
        candidate.environmentId === leaf.environmentId &&
        candidate.id === leaf.threadId &&
        candidate.worldKey === input.worldKey,
    );
    if (
      shell !== undefined &&
      shell.sessionId != null &&
      shell.archivedAt === null &&
      engagedSession(shell.sessionId)
    ) {
      return { kind: "thread", environmentId: leaf.environmentId, threadId: leaf.threadId };
    }
  }
  const entry =
    entrySessionOf(input.index)?.sessionId ??
    input.index.sessions[0]?.sessionId ??
    HARMONY_FIRST_SESSION_ID;
  return { kind: "session", sessionId: entry };
}

/**
 * Where a ready World's view moves the page, or null when the page stays: the root and an unbound leaf go to the World's
 * restored room (resolveWorldEntryView), an explicit session address is never replaced while its own read arrives, and a
 * bound leaf stays. cleo-web (webHost.ts) is the exception at its root: `/` is the browser host's landing, the person's
 * World conversations and New (WebHostLanding.tsx), so a World read that comes back ready must not carry the page off to
 * the World's first session. W-7703fa11: it did, about four seconds after the page opened, and New's new conversation
 * opened on a page that had already left for /session/S_harmony_session.
 */
export function resolveWorldEntryRelocation(input: {
  readonly worldKey: string;
  readonly index: WorldRoomDirectory;
  readonly activeSessionId: string;
  readonly pathname: string;
  readonly shells: ReadonlyArray<EnvironmentThreadShell>;
  readonly remembered: string;
  readonly webHost: boolean;
}): WorldEntryView | null {
  if (input.webHost && input.pathname === "/") return null;
  const leaf = parseSessionSessionLeafPath(input.pathname);
  const bound =
    (leaf?.kind === "session" && leaf.sessionId === input.activeSessionId) ||
    (leaf?.kind === "thread" &&
      input.shells.some(
        (shell) =>
          shell.environmentId === leaf.environmentId &&
          shell.id === leaf.threadId &&
          shell.worldKey === input.worldKey &&
          shell.sessionId === input.activeSessionId,
      ));
  if (input.pathname !== "/" && leaf === null) return null;
  // An explicit session address must never be replaced by a fallback
  // while its independent read is still arriving.
  if (bound || leaf?.kind === "session") return null;
  return input.pathname === "/"
    ? resolveWorldEntryView({
        index: input.index,
        worldKey: input.worldKey,
        shells: input.shells,
        remembered: input.remembered,
      })
    : { kind: "session", sessionId: input.activeSessionId };
}

/**
 * Local mode's remembered room (WS9): only a live ungoverned session leaf
 * restores — the thread must exist, belong to no World, and not be archived.
 * Anything else returns null and the draft landing takes over. Drafts are
 * minted fresh, never restored by path.
 */
export function resolveLocalEntryView(input: {
  readonly shells: ReadonlyArray<EnvironmentThreadShell>;
  readonly remembered: string;
}): { readonly kind: "thread"; readonly environmentId: string; readonly threadId: string } | null {
  const leaf = parseSessionSessionLeafPath(input.remembered);
  if (leaf?.kind !== "thread") return null;
  const shell = input.shells.find(
    (candidate) => candidate.environmentId === leaf.environmentId && candidate.id === leaf.threadId,
  );
  if (shell === undefined || shell.worldKey !== null || shell.archivedAt !== null) return null;
  return { kind: "thread", environmentId: leaf.environmentId, threadId: leaf.threadId };
}

/**
 * The routed /settings/* page is Local chrome (principal ruling 2026-08-27:
 * the settings left pane appears only in Local mode). A World whose room
 * reads back relocates the page into that World's restored room, where the
 * Canvas hosts Settings as a tab (WS5). A loading or unreadable room returns
 * null so the routed page stays reachable to fix the connection — the
 * refusal card's "Open Connections" door must never enter a relocation loop.
 */
export function resolveRoutedSettingsRelocation(input: {
  readonly room: WorldViewState;
  readonly shells: ReadonlyArray<EnvironmentThreadShell>;
}): WorldEntryView | null {
  if (input.room.kind !== "world-ready") return null;
  return resolveWorldEntryView({
    index: input.room.index,
    worldKey: input.room.worldKey,
    shells: input.shells,
    remembered: readWorldRoom(input.room.worldKey),
  });
}

export function WorldViewProvider({ children }: { readonly children: ReactNode }): ReactElement {
  useEffect(() => startSessionDeckSync(), []);
  const loaded = useNeucleosWorldStore((state) => state.loaded);
  const activeWorldKey = useNeucleosWorldStore((state) => state.activeWorldKey);
  const grantRevision = useNeucleosWorldStore((state) => state.grantRevision);
  const identity = useNeucleosWorldStore((state) => state.identity);
  // The ONE mode derivation (F04): signed-out is Local, the chosen Local door
  // is Local, and a signed-in Actor with zero Worlds is Local (W06 — never a
  // refusal card). A selected World whose room cannot be read stays a
  // (refused) World — it never silently becomes Local.
  const localMode = useNeucleosWorldStore((state) =>
    localModeActive({
      signedIn: state.signedIn,
      worldCount: state.worlds.length,
      selection: state.selection,
    }),
  );
  const shells = useThreadShells();
  const pathname = useLocation({ select: (location) => location.pathname });
  const navigate = useNavigate();
  const [revision, setRevision] = useState(0);
  // A refresh of a ready view: the World read again behind what is shown.
  const [quietRevision, setQuietRevision] = useState(0);
  const waitId = useRef(0);
  const [pending, setPending] = useState<{ id: number; scope: string; label: string } | null>(null);
  const entryScope = JSON.stringify([activeWorldKey, identity, grantRevision]);
  const liveEntryScope = useRef(entryScope);
  liveEntryScope.current = entryScope;
  useEffect(() => {
    liveEntryScope.current = entryScope;
    return () => {
      liveEntryScope.current = "";
    };
  }, [entryScope]);
  const [entryReadbacks, setEntryReadbacks] = useState<{
    scope: string;
    rows: ReadonlyArray<WorldRoomEntry>;
  }>({ scope: "", rows: [] });
  const [read, setRead] = useState<WorldRoomRead>({ status: "idle" });
  const readStatus = useRef<WorldRoomRead["status"]>("idle");
  readStatus.current = read.status;
  const [directory, setDirectory] = useState<Extract<WorldRoomRead, { status: "ready" }> | null>(
    null,
  );
  useEffect(() => {
    if (read.status === "ready") setDirectory(read);
    if (read.status === "unavailable" && read.refusalKind !== "transport") setDirectory(null);
  }, [read]);
  // A forgotten remembered room changes what readWorldRoom answers; this
  // counter makes the next render read it again.
  const [, setRoomMemoryEpoch] = useState(0);
  const pathnameNow = useRef(pathname);
  pathnameNow.current = pathname;
  const navigateNow = useRef(navigate);
  navigateNow.current = navigate;
  const selectedLeaf = parseSessionSessionLeafPath(
    pathname === "/" && activeWorldKey ? readWorldRoom(activeWorldKey) : pathname,
  );
  const selectedSessionId =
    selectedLeaf?.kind === "session"
      ? selectedLeaf.sessionId
      : selectedLeaf?.kind === "thread"
        ? (shells.find(
            (shell) =>
              shell.worldKey === activeWorldKey &&
              shell.environmentId === selectedLeaf.environmentId &&
              shell.id === selectedLeaf.threadId,
          )?.sessionId ?? null)
        : null;
  // A door's request to front a room's main tab (requestDeckFront) is for the
  // visit it opened: going to any other session, or out of the World, ends it.
  useEffect(() => {
    keepDeckFrontOnlyFor(
      localMode || !activeWorldKey || !selectedSessionId
        ? null
        : { worldKey: activeWorldKey, sessionId: selectedSessionId },
    );
  }, [activeWorldKey, localMode, selectedSessionId]);
  const [selectedRead, setSelectedRead] = useState<{
    scope: string;
    sessionId: string;
    entries?: ReadonlyArray<WorldRoomEntry>;
    refusal?: { kind: string; reason: string };
  } | null>(null);
  // This read serves the addressed session independently of directory size.
  // Ancestors and their saved decks must arrive before any canvas can seed.
  useEffect(() => {
    if (!loaded || localMode || !activeWorldKey || !selectedSessionId) return;
    let current = true;
    const isCurrent = () => current && liveEntryScope.current === entryScope;
    setSelectedRead(null);
    void (async () => {
      const entries: WorldRoomEntry[] = [];
      let sessionId = selectedSessionId;
      let roomId: string | undefined;
      while (true) {
        if (
          entries.length >= 64 ||
          entries.some((entry) => entry.session.sessionId === sessionId)
        ) {
          if (isCurrent())
            setSelectedRead({
              scope: entryScope,
              sessionId: selectedSessionId,
              refusal: {
                kind: "invalid_room",
                reason: "The selected Session ancestry is invalid.",
              },
            });
          return;
        }
        const outcome = await readWorldRoomEntry({
          worldKey: activeWorldKey,
          sessionId,
          ...(roomId === undefined ? {} : { roomId }),
        });
        if (!isCurrent()) return;
        if (!outcome.ok) {
          // The address came from this World's room memory (the route is the
          // landing path), and the World refuses it as not its own: the memory
          // is wrong, so forget it and fall back to the World's own starting
          // session. An explicit address or a transient transport failure is
          // left alone (the latter keeps its Retry).
          //
          // A World switch carries that memory as the route itself
          // (enterWorld), and the frame files the page under the World again,
          // so a route equal to this World's own slot is the same memory, not
          // an address the person typed. Drop the slot and return to "/" so
          // the World's room index chooses the room; Retry read heals it the
          // same way. A different explicit address is left alone.
          if (outcome.kind !== "transport") {
            if (pathnameNow.current === "/") {
              forgetWorldRoom(activeWorldKey);
              setRoomMemoryEpoch((value) => value + 1);
              return;
            }
            if (pathnameNow.current === readWorldRoom(activeWorldKey)) {
              forgetWorldRoom(activeWorldKey);
              setRoomMemoryEpoch((value) => value + 1);
              void navigateNow.current({ to: "/", replace: true } as never);
              return;
            }
          }
          setSelectedRead({ scope: entryScope, sessionId: selectedSessionId, refusal: outcome });
          return;
        }
        entries.push(outcome.entry);
        roomId = outcome.entry.room.roomId;
        if (outcome.entry.session.parentId === roomId) break;
        sessionId = outcome.entry.session.parentId;
      }
      const restored = await Promise.all(
        entries.map((entry) =>
          hydrateWorldSessionDecks(activeWorldKey, fetch, entry.session.sessionId, isCurrent),
        ),
      );
      if (!isCurrent()) return;
      setSelectedRead(
        restored.every(Boolean)
          ? { scope: entryScope, sessionId: selectedSessionId, entries }
          : {
              scope: entryScope,
              sessionId: selectedSessionId,
              refusal: {
                kind: "view_prefs_unavailable",
                reason: "The saved session tabs could not be read. Retry the World connection.",
              },
            },
      );
    })();
    return () => {
      current = false;
    };
  }, [activeWorldKey, entryScope, loaded, localMode, selectedSessionId, revision]);

  useEffect(() => {
    if (read.status !== "ready") return;
    setEntryReadbacks((current) => {
      if (current.scope !== entryScope) return current;
      const rows = current.rows.filter((entry) => entry.cut > read.index.cut);
      return rows.length === current.rows.length ? current : { ...current, rows };
    });
  }, [read, entryScope]);

  // Only root title changes can announce background World naming. Token,
  // status and other shell updates must not refetch the World or its decks.
  const rootTitlesKey = JSON.stringify(
    shells
      .filter(
        (shell) =>
          shell.worldKey === activeWorldKey &&
          shell.sessionId &&
          !shell.parentThreadId &&
          !shell.localWorkPolicy,
      )
      .map((shell) => [JSON.stringify([shell.environmentId, shell.id]), shell.title])
      .sort(([left], [right]) => left!.localeCompare(right!)),
  );
  const seenRootTitles = useRef<{ scope: string; titles: Map<string, string> } | null>(null);
  const refusedNameReadScope = useRef<string | null>(null);
  useEffect(() => {
    if (!loaded || localMode || activeWorldKey === null) {
      seenRootTitles.current = null;
      refusedNameReadScope.current = null;
      return;
    }
    const scope = JSON.stringify([activeWorldKey, grantRevision]);
    const titles = new Map<string, string>(JSON.parse(rootTitlesKey));
    const previous = seenRootTitles.current;
    seenRootTitles.current = { scope, titles };
    if (previous?.scope !== scope) refusedNameReadScope.current = null;
    if (
      previous?.scope !== scope ||
      ![...titles].some(
        ([id, title]) => previous.titles.has(id) && previous.titles.get(id) !== title,
      )
    )
      return;
    if (refusedNameReadScope.current === scope) return;
    let current = true;
    void readWorldRoomIndex(activeWorldKey, undefined, { isCurrent: () => current }).then(
      (outcome) => {
        if (!current) return;
        if (
          !outcome.ok &&
          [
            "not_signed_in",
            "world_unresolved",
            "grant_stale",
            "tool_not_on_grant",
            "grant_rejected",
          ].includes(outcome.kind)
        )
          refusedNameReadScope.current = scope;
        setRead((existing) => {
          if (existing.status !== "ready" || existing.worldKey !== activeWorldKey) return existing;
          if (!outcome.ok)
            return {
              status: "unavailable",
              scope: entryScope,
              worldKey: activeWorldKey,
              reason: outcome.reason,
              refusalKind: outcome.kind,
            };
          if (outcome.index.worldKey !== activeWorldKey || outcome.index.cut < existing.index.cut)
            return existing;
          return {
            status: "ready",
            scope: entryScope,
            worldKey: activeWorldKey,
            index: outcome.index,
          };
        });
      },
    );
    return () => {
      current = false;
    };
  }, [activeWorldKey, entryScope, grantRevision, loaded, localMode, rootTitlesKey]);

  useEffect(() => {
    if (!loaded || localMode || activeWorldKey === null) {
      setRead({ status: "idle" });
      return;
    }
    let current = true;
    setRead({ status: "loading" });
    void readRoomsWithDecks(
      activeWorldKey,
      entryScope,
      () => current,
      // Pages never overwrite a read that already settled (a readback, a refusal).
      (partial) =>
        setRead((existing) =>
          existing.status === "loading" || existing.status === "partial" ? partial : existing,
        ),
    ).then((next) => {
      if (next !== null) setRead(next);
    });
    return () => {
      current = false;
    };
  }, [activeWorldKey, entryScope, grantRevision, loaded, localMode, revision]);

  // The quiet refresh: the whole directory, swapped in only when it is complete,
  // of this World and no older than what is shown. A read that fails keeps
  // what is shown; the next refresh asks again.
  useEffect(() => {
    if (quietRevision === 0 || !loaded || localMode || activeWorldKey === null) return;
    let current = true;
    void readWorldRoomIndex(activeWorldKey, undefined, { isCurrent: () => current }).then(
      (outcome) => {
        if (!current || !outcome.ok) return;
        setRead((existing) =>
          existing.status !== "ready" ||
          existing.worldKey !== activeWorldKey ||
          outcome.index.worldKey !== activeWorldKey ||
          outcome.index.cut < existing.index.cut
            ? existing
            : {
                status: "ready",
                scope: entryScope,
                worldKey: activeWorldKey,
                index: outcome.index,
              },
        );
      },
    );
    return () => {
      current = false;
    };
  }, [activeWorldKey, entryScope, loaded, localMode, quietRevision]);

  // Whichever read met a transport refusal, the view re-reads on its own
  // (TRANSPORT_RETRY_DELAYS_MS). Any other change to the read — a scope
  // change, a manual Retry, an accepted readback — cancels the pending one.
  useEffect(() => {
    if (read.status !== "unavailable" || read.refusalKind !== "transport") return;
    const retried = read.retried ?? 0;
    const delay = TRANSPORT_RETRY_DELAYS_MS[retried];
    if (delay === undefined) return;
    const worldKey = read.worldKey;
    let current = true;
    const timer = setTimeout(() => {
      void readRoomsWithDecks(worldKey, read.scope, () => current).then((next) => {
        if (next === null) return;
        setRead(
          next.status === "unavailable" && next.refusalKind === "transport"
            ? { ...next, retried: retried + 1 }
            : next,
        );
      });
    }, delay);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [read]);

  const state = useMemo<WorldViewState>(() => {
    if (!loaded) return { kind: "world-loading", worldKey: activeWorldKey ?? "pending" };
    if (localMode) return { kind: "local" };
    if (activeWorldKey === null) {
      return {
        kind: "world-unavailable",
        worldKey: null,
        reason: "Choose an active World before entering a governed room.",
        refusalKind: null,
      };
    }
    const selected =
      selectedRead?.scope === entryScope && selectedRead.sessionId === selectedSessionId
        ? selectedRead
        : null;
    const directoryRefused =
      read.status === "unavailable" &&
      read.scope === entryScope &&
      read.worldKey === activeWorldKey &&
      read.refusalKind !== "transport";
    if (selected?.refusal && selected.refusal.kind !== "transport") {
      return {
        kind: "world-unavailable",
        worldKey: activeWorldKey,
        reason: selected.refusal.reason,
        refusalKind: selected.refusal.kind,
      };
    }
    const base =
      read.status === "ready" && read.scope === entryScope
        ? read
        : directory?.scope === entryScope
          ? directory
          : null;
    const partialPages =
      base === null && read.status === "partial" && read.scope === entryScope ? read : null;
    if (!directoryRefused && partialPages && partialPages.worldKey === activeWorldKey) {
      const index = displayWorldRoomPagesWithEntries(partialPages.pages, [
        ...(entryReadbacks.scope === entryScope ? entryReadbacks.rows : []),
        ...(selected?.entries ?? []),
      ]);
      // Ready from pages only once the session this view lands on is among
      // them: the addressed one, or (a bare launch) the World's entry session.
      const landed =
        selectedSessionId !== null
          ? index.sessions.some((row) => row.sessionId === selectedSessionId)
          : entrySessionOf(index) !== undefined;
      if (landed)
        return {
          kind: "world-ready",
          worldKey: activeWorldKey,
          index,
          activeSessionId: resolveActiveWorldSession({
            index,
            worldKey: activeWorldKey,
            pathname,
            shells,
          }),
        };
      if (selected?.refusal)
        return {
          kind: "world-unavailable",
          worldKey: activeWorldKey,
          reason: selected.refusal.reason,
          refusalKind: selected.refusal.kind,
        };
      return { kind: "world-loading", worldKey: activeWorldKey };
    }
    if (!directoryRefused && base && base.worldKey === activeWorldKey) {
      const index = displayWorldRoomEntries(base.index, [
        ...(entryReadbacks.scope === entryScope ? entryReadbacks.rows : []),
        ...(selected?.entries ?? []),
      ]);
      // A complete directory can arrive before the addressed entry. Do not
      // lend another session's active identity to this explicit address.
      // Keep the directory cached for its rail when the exact read completes.
      if (selectedSessionId && !index.sessions.some((row) => row.sessionId === selectedSessionId)) {
        if (selected?.refusal || selected?.entries) {
          return {
            kind: "world-unavailable",
            worldKey: activeWorldKey,
            reason:
              selected.refusal?.reason ??
              "The selected Session is absent from the current World directory.",
            refusalKind: selected.refusal?.kind ?? "session_absent",
          };
        }
        return { kind: "world-loading", worldKey: activeWorldKey };
      }
      return {
        kind: "world-ready",
        worldKey: activeWorldKey,
        index,
        activeSessionId: resolveActiveWorldSession({
          index,
          worldKey: activeWorldKey,
          pathname,
          shells,
        }),
      };
    }
    if (!directoryRefused && selected?.entries && selectedSessionId) {
      return {
        kind: "world-ready",
        worldKey: activeWorldKey,
        index: displaySelectedWorldEntries([
          ...selected.entries,
          ...(entryReadbacks.scope === entryScope ? entryReadbacks.rows : []),
        ]),
        activeSessionId: selectedSessionId,
      };
    }
    if (selected?.refusal && read.status !== "unavailable") {
      return {
        kind: "world-unavailable",
        worldKey: activeWorldKey,
        reason: selected.refusal.reason,
        refusalKind: selected.refusal.kind,
      };
    }
    if (
      read.status === "unavailable" &&
      read.scope === entryScope &&
      read.worldKey === activeWorldKey
    ) {
      const retried = read.retried ?? 0;
      return {
        kind: "world-unavailable",
        worldKey: activeWorldKey,
        reason: read.reason,
        refusalKind: read.refusalKind,
        retry:
          read.refusalKind === "transport" && retried < TRANSPORT_RETRY_DELAYS_MS.length
            ? { attempt: retried + 1, of: TRANSPORT_RETRY_DELAYS_MS.length }
            : null,
      };
    }
    return { kind: "world-loading", worldKey: activeWorldKey };
  }, [
    activeWorldKey,
    loaded,
    localMode,
    pathname,
    read,
    shells,
    entryReadbacks,
    entryScope,
    selectedRead,
    selectedSessionId,
    directory,
  ]);

  // A signed-in World opens on its own restored room: the remembered room
  // validated against the live index (WS8), else its authoritative Harmony
  // Session. A legacy unbound local thread/draft is never used as a fallback
  // room. cleo-web's root is its own landing and is never relocated
  // (resolveWorldEntryRelocation, W-7703fa11).
  useEffect(() => {
    if (state.kind !== "world-ready") return;
    const entry = resolveWorldEntryRelocation({
      worldKey: state.worldKey,
      index: state.index,
      activeSessionId: state.activeSessionId,
      pathname,
      shells,
      remembered: readWorldRoom(state.worldKey),
      webHost: isWebHost(),
    });
    if (entry === null) return;
    if (entry.kind === "thread") {
      void navigate({
        to: "/$environmentId/$threadId",
        params: { environmentId: entry.environmentId, threadId: entry.threadId },
        replace: true,
      } as never);
      return;
    }
    void navigate({
      to: "/session/$sessionId",
      params: { sessionId: entry.sessionId },
      replace: true,
    } as never);
  }, [navigate, pathname, shells, state]);

  // Can 80's Tester (18 Oct 2026): a refresh that reset the view to loading
  // showed a half-read rail for about 25 s and took the keyboard out of Cleo's
  // composer, right after her tidy. A ready view now reads the World again
  // behind what it shows and swaps in the whole read; any other view reloads.
  const refresh = useCallback(() => {
    if (readStatus.current === "ready") setQuietRevision((value) => value + 1);
    else setRevision((value) => value + 1);
  }, []);
  const beginWait = useCallback(
    (label: string) => {
      const id = ++waitId.current;
      setPending({ id, scope: entryScope, label });
      return () => setPending((current) => (current?.id === id ? null : current));
    },
    [entryScope],
  );
  const acceptReadback = useCallback(
    (index: WorldRoomIndex) => {
      if (index.worldKey !== activeWorldKey || liveEntryScope.current !== entryScope) return;
      setRead({ status: "ready", scope: entryScope, worldKey: index.worldKey, index });
    },
    [activeWorldKey, entryScope],
  );
  const acceptEntryReadback = useCallback(
    (entry: WorldRoomEntry) => {
      if (entry.worldKey !== activeWorldKey || liveEntryScope.current !== entryScope) return false;
      setEntryReadbacks((current) => {
        const rows = current.scope === entryScope ? current.rows : [];
        const prior = rows.find((row) => row.session.sessionId === entry.session.sessionId);
        if (prior && prior.cut > entry.cut) return current;
        return {
          scope: entryScope,
          rows: [...rows.filter((row) => row.session.sessionId !== entry.session.sessionId), entry],
        };
      });
      return true;
    },
    [activeWorldKey, entryScope],
  );
  // The principal, 16 Oct 2026 ("showing session that cleo launches for a
  // hand, as a nested item under the main workroom session"): a session Cleo
  // opens on the server (create_child_session, dispatch_provider_targets)
  // reaches this Desk first as a thread; the room index would learn of it only
  // on a full reread of every page. Once the index is complete, each thread of
  // this World whose session the index lacks has that session's entry read
  // once and accepted, so it stands in the rail as soon as Cleo starts it.
  // One-backend plan step 3, 4b-ii: so does each of the person's conversations
  // kept in the World (a World helper Cleo started there has no thread here).
  const sessionEntriesAsked = useRef<{ scope: string; ids: Set<string> }>({
    scope: "",
    ids: new Set(),
  });
  // Can 23: a Pocket conversation's World session is labelled "Pocket conversation" by the World; the Desk names it by
  // the conversation's own title, as Pocket does.
  const cloudConversations = useCloudConversations(
    state.kind === "world-ready" ? state.worldKey : null,
  );
  useEffect(() => {
    if (state.kind !== "world-ready" || !isWorldRoomDisplayComplete(state.index)) return;
    if (sessionEntriesAsked.current.scope !== entryScope)
      sessionEntriesAsked.current = { scope: entryScope, ids: new Set() };
    const asked = sessionEntriesAsked.current.ids;
    const worldKey = state.worldKey;
    const missing = sessionEntriesToRead({
      worldKey,
      known: new Set(state.index.sessions.map((session) => session.sessionId)),
      asked,
      shells,
      worldConversationSessions: [...cloudConversations.keys()],
    });
    for (const sessionId of missing) {
      asked.add(sessionId);
      void readWorldRoomEntry({ worldKey, sessionId }).then((outcome) => {
        if (outcome.ok) acceptEntryReadback(outcome.entry);
      });
    }
  }, [acceptEntryReadback, cloudConversations, entryScope, shells, state]);
  // FD44 (2026-09-05): the controller's workroom door is seated HERE —
  // this provider holds the three things the door needs (the ready index,
  // the readback acceptor, the router), so chrome that holds none of them
  // (the context dock's Music seat, the strip's expand doors, a transcript
  // card) opens a workroom through openWorldViewWorkroom. The room's
  // existing session is addressed — a person opening their player wants it
  // back, not a new conversation (RM9: "click to restore existing"). Local,
  // loading and unavailable answer false so the caller may teach; a refused
  // door says its sentence instead of failing quietly.
  // The opening in flight. A second press of the same room's door joins it
  // rather than starting another; the newest press's intent is the one that
  // holds, including after the first has already navigated.
  const workroomPending = useRef<{
    readonly scope: string;
    readonly key: string;
    intent: WorkroomOpenIntent | undefined;
    navigatedTo: string | null;
  } | null>(null);
  useEffect(
    () =>
      seatWorkroomDoor((workroomKey, requestedWorldKey, intent) => {
        // Pages still arriving are enough: a workroom the pages do not hold yet is
        // fronted from the World's own refusal of a second one (W-e978d3d2).
        if (
          state.kind !== "world-ready" ||
          liveEntryScope.current !== entryScope ||
          (requestedWorldKey !== undefined && requestedWorldKey !== state.worldKey)
        )
          return false;
        const worldKey = state.worldKey;
        // `tab: "main"` (the rail player's expand) asks the deck layer to
        // front the room's own board over the session's restored deck, from
        // whichever read restores it; any other door withdraws such a request
        // and the restored last tab stands. A room whose board is Goals keeps
        // its deck's own default.
        const frontFor = (sessionId: string, wanted: WorkroomOpenIntent | undefined) => {
          const kind = deckDefaultBoardKind(workroomKey, worldKey);
          if (wanted?.tab === "main" && kind !== "goals")
            requestDeckFront({ worldKey, sessionId }, kind);
          else withdrawDeckFront();
        };
        const inFlight = workroomPending.current;
        if (inFlight?.scope === entryScope && inFlight.key === workroomKey) {
          inFlight.intent = intent;
          if (inFlight.navigatedTo !== null) frontFor(inFlight.navigatedTo, intent);
          return true;
        }
        const attempt = {
          scope: entryScope,
          key: workroomKey,
          intent,
          navigatedTo: null as string | null,
        };
        workroomPending.current = attempt;
        withdrawDeckFront();
        const isCurrent = () =>
          liveEntryScope.current === entryScope && workroomPending.current === attempt;
        const title = workroomTitleForBoard(worldKey, workroomKey) ?? workroomKey;
        const finishWait = beginWait(`Opening ${title}…`);
        void activateOrCreateWorkroom({
          worldKey,
          index: state.index,
          workroomKey,
          title,
          freshSession: false,
          acceptReadback: (index) => {
            if (isCurrent()) acceptReadback(index);
          },
          acceptEntryReadback: (entry) => isCurrent() && acceptEntryReadback(entry),
          navigate: ({ sessionId }) => {
            if (!isCurrent()) return;
            attempt.navigatedTo = sessionId;
            frontFor(sessionId, attempt.intent);
            return navigate({
              to: "/session/$sessionId",
              params: { sessionId },
              search: { world: worldKey },
            } as never);
          },
        })
          .then((outcome) => {
            if (!isCurrent() || outcome.ok) return;
            toastManager.add({
              type: "error",
              title: `${title} could not be opened`,
              description: outcome.reason,
            });
          })
          .catch(() => {
            if (!isCurrent()) return;
            toastManager.add({
              type: "error",
              title: `${title} could not be opened`,
              description: "The session could not be confirmed. Refresh before retrying.",
            });
          })
          .finally(() => {
            finishWait();
            if (workroomPending.current === attempt) workroomPending.current = null;
          });
        return true;
      }),
    [acceptReadback, acceptEntryReadback, beginWait, entryScope, navigate, state],
  );
  const pendingLabel =
    pending?.scope === entryScope && state.kind === "world-ready" ? pending.label : null;
  const shown = useMemo(
    () => withCloudConversationTitles(state, cloudConversations),
    [cloudConversations, state],
  );
  const value = useMemo(
    () => ({ state: shown, pendingLabel, beginWait, refresh, acceptReadback, acceptEntryReadback }),
    [acceptReadback, acceptEntryReadback, beginWait, pendingLabel, refresh, shown],
  );
  return <WorldViewContext.Provider value={value}>{children}</WorldViewContext.Provider>;
}

/**
 * The sessions whose entries the index lacks, read once each, at most 8 at a time: each live thread of this World with a
 * session, then each of the person's conversations kept in the World (by its World session). Pure.
 */
export function sessionEntriesToRead(input: {
  readonly worldKey: string;
  readonly known: ReadonlySet<string>;
  readonly asked: ReadonlySet<string>;
  readonly shells: ReadonlyArray<{
    readonly worldKey?: string | null | undefined;
    readonly archivedAt?: unknown;
    readonly sessionId?: unknown;
  }>;
  readonly worldConversationSessions: ReadonlyArray<string>;
}): ReadonlyArray<string> {
  const fromThreads = input.shells.flatMap((shell) =>
    shell.worldKey === input.worldKey &&
    shell.archivedAt == null &&
    typeof shell.sessionId === "string"
      ? [shell.sessionId]
      : [],
  );
  return [...new Set([...fromThreads, ...input.worldConversationSessions])]
    .filter((sessionId) => !input.known.has(sessionId) && !input.asked.has(sessionId))
    .slice(0, 8);
}

/** The World view with each Pocket conversation's session under its conversation's title (Can 23). Pure. */
export function withCloudConversationTitles(
  state: WorldViewState,
  conversations: ReadonlyMap<string, { readonly title: string }>,
): WorldViewState {
  if (state.kind !== "world-ready" || conversations.size === 0) return state;
  let changed = false;
  const sessions = state.index.sessions.map((session) => {
    const title = conversations.get(session.sessionId)?.title;
    if (!title || title === session.label) return session;
    changed = true;
    return { ...session, label: title };
  });
  return changed ? { ...state, index: { ...state.index, sessions } } : state;
}

/**
 * True once every room of the World is in. While pages are still arriving (or
 * only the addressed session's entry has been read) the rail, the attention
 * zone, Kanban, the palette and the Goals view hold only part of the World:
 * they say so (`data-world-rooms-loading`) instead of showing an empty or
 * "nothing needs you" answer. Outside a ready World there is nothing to claim.
 */
export function useWorldRoomsComplete(): boolean {
  const value = useContext(WorldViewContext);
  if (value === null || value.state.kind !== "world-ready") return true;
  return isWorldRoomDisplayComplete(value.state.index);
}

export function useWorldView(): WorldViewValue {
  const value = useContext(WorldViewContext);
  if (value === null) throw new Error("World world view is outside its provider");
  return value;
}

/**
 * FD-CAN-15: the nullable peek for ALWAYS-MOUNTED chrome (the DeskDock's
 * Sessions section) that must render quietly wherever no room provider is
 * seated — never a second authority, only the same context read without
 * the throw.
 */
export function useWorldViewIfSeated(): WorldViewValue | null {
  return useContext(WorldViewContext);
}
